import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REASONS = [
  'I no longer play the game',
  "I'm worried about my privacy",
  'I want to start over with a new account',
  'I had a bad experience',
  'Other (please describe below)',
];

const DeleteAccountPage = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [reason, setReason] = useState(REASONS[0]);
  const [details, setDetails] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);

  const canSubmit =
    !!email && /\S+@\S+\.\S+/.test(email) && acknowledged && !submitting;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/account/deletion-request`, {
        email: email.trim(),
        display_name: displayName.trim() || undefined,
        reason,
        details: details.trim() || undefined,
        acknowledged: true,
      });
      setSubmittedId(data.request_id);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Submission failed';
      toast({ title: 'Could not submit', description: msg, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedId) {
    return (
      <div className="pt-28 pb-20 bg-[#0a0608] min-h-screen">
        <div className="max-w-[640px] mx-auto px-6 lg:px-10 text-center fade-up">
          <CheckCircle2 size={56} className="text-[#f5b400] mx-auto" />
          <h1 className="font-heading text-white text-3xl md:text-4xl mt-5">Request received</h1>
          <p className="mt-4 font-body text-white/80 leading-relaxed">
            Thanks — your account deletion request was forwarded to our team. We will
            process it within <b>14 days</b> and send you a final confirmation email
            once the records have been wiped.
          </p>
          <p className="mt-4 font-body text-white/55 text-sm">
            Reference: <code className="font-mono text-[#f5b400]">{submittedId.slice(0, 8)}</code>
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 bg-[#f5b400] hover:bg-[#ffc630] text-black font-nav text-xs px-6 py-3 rounded-md transition-colors"
            data-testid="deletion-back-home"
          >
            <ArrowLeft size={14} /> BACK HOME
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-20 bg-[#0a0608] min-h-screen">
      <div className="max-w-[760px] mx-auto px-6 lg:px-10 font-body">
        <Link
          to="/terms"
          className="inline-flex items-center gap-2 text-[#f5b400] font-nav text-xs hover:text-white transition-colors"
          data-testid="deletion-back-link"
        >
          <ArrowLeft size={14} /> BACK TO PRIVACY POLICY
        </Link>

        <div className="mt-6 text-center">
          <p className="font-nav text-[#f5b400] text-xs tracking-widest inline-flex items-center gap-2 justify-center">
            <Trash2 size={12} /> ACCOUNT &amp; DATA
          </p>
          <h1 className="font-heading section-heading-white mt-3">DELETE</h1>
          <h1 className="font-heading section-heading-gold">YOUR ACCOUNT</h1>
        </div>

        {/* Warning banner */}
        <div
          className="mt-8 rounded-lg border border-red-500/50 bg-red-950/30 p-5"
          data-testid="deletion-warning"
        >
          <div className="flex gap-3 items-start">
            <AlertTriangle className="text-red-400 shrink-0 mt-0.5" size={22} />
            <div>
              <h3 className="font-heading text-red-300 text-lg">This cannot be undone</h3>
              <p className="text-white/80 text-sm mt-1.5 leading-relaxed">
                Submitting this form will permanently erase your character, level,
                inventory, Sapphires, friends list, and every other piece of progress
                tied to your account. There is no recovery, no rollback, and no undo.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="mt-8 rounded-2xl border border-white/10 bg-black/40 p-6 md:p-8 space-y-5">
          <div>
            <label className="font-nav text-[10px] tracking-widest text-white/55">
              EMAIL USED FOR SIGN-IN *
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="deletion-email"
              placeholder="you@example.com"
              className="mt-1 w-full bg-black/50 border border-white/15 focus:border-[#f5b400] outline-none rounded-md px-3 py-2.5 text-white text-sm placeholder:text-white/30 transition-colors"
            />
          </div>

          <div>
            <label className="font-nav text-[10px] tracking-widest text-white/55">
              IN-GAME DISPLAY NAME
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value.slice(0, 40))}
              data-testid="deletion-display-name"
              placeholder="e.g. Andrei Tepes"
              className="mt-1 w-full bg-black/50 border border-white/15 focus:border-[#f5b400] outline-none rounded-md px-3 py-2.5 text-white text-sm placeholder:text-white/30 transition-colors"
            />
          </div>

          <div>
            <label className="font-nav text-[10px] tracking-widest text-white/55">
              REASON
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="deletion-reason"
              className="mt-1 w-full bg-black/50 border border-white/15 focus:border-[#f5b400] outline-none rounded-md px-3 py-2.5 text-white text-sm transition-colors"
            >
              {REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="font-nav text-[10px] tracking-widest text-white/55">
              ADDITIONAL DETAILS (OPTIONAL)
            </label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value.slice(0, 800))}
              rows={4}
              data-testid="deletion-details"
              placeholder="Anything else we should know? Issues, feedback…"
              className="mt-1 w-full bg-black/50 border border-white/15 focus:border-[#f5b400] outline-none rounded-md px-3 py-2.5 text-white text-sm placeholder:text-white/30 transition-colors resize-none"
            />
            <p className="text-right text-white/40 text-[10px] mt-1">{details.length}/800</p>
          </div>

          <label className="flex gap-3 items-start cursor-pointer select-none" data-testid="deletion-ack-label">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              data-testid="deletion-ack"
              className="mt-1 h-4 w-4 accent-[#f5b400] cursor-pointer"
            />
            <span className="text-white/80 text-sm leading-snug">
              I understand that submitting this request will permanently and
              irreversibly delete my New Realm Online account and all in-game
              progress, including Sapphires I have purchased.
            </span>
          </label>

          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="deletion-submit"
            className="w-full bg-red-500 hover:bg-red-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-nav text-xs py-3.5 rounded-md inline-flex items-center justify-center gap-2 transition-colors"
          >
            <Trash2 size={14} />
            {submitting ? 'SUBMITTING…' : 'SUBMIT DELETION REQUEST'}
          </button>

          <p className="text-center text-white/45 text-[11px]">
            We will process your request within 14 days. Questions?{' '}
            <a
              href="mailto:newrealmonlinecs@gmail.com"
              className="text-[#f5b400] hover:underline"
            >
              newrealmonlinecs@gmail.com
            </a>
          </p>
        </form>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
